import React, { useState } from "react";
import { Bar } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

const corTexto = "#01054C";
const corGrid  = "#E5E7EB";

const formatarM = (v) => {
  if (v == null || v === 0) return "";
  if (v >= 1_000_000)
    return `R$${(v / 1_000_000).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}M`;
  if (v >= 1_000)
    return `R$${(v / 1_000).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 1 })}k`;
  return `R$${String(v)}`;
};

const truncar = (nome, max = 18) =>
  nome.length > max ? nome.substring(0, max) + "..." : nome;

const coresEscuras = new Set(["#A8E0FF", "#9DA2B6"]);

const formatarReais = (v) => {
  if (v == null || v === 0) return "";
  return `R$${new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2 }).format(v)}`;
};



const coresClientes = ["#00034D", "#034EA2", "#66CCFF", "#9DA2B6", "#7EC83A"];

const TopClientesTransacao = ({ itens = [] }) => {
  const [ocultos, setOcultos] = useState(new Set());

  const toggle = (idx) =>
    setOcultos((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });

  const coresItem      = itens.map((_, idx) => coresClientes[idx] ?? coresClientes[0]);
  const nomesCompletos = itens.map((i) => i.nome);

  const dados = {
    labels: itens.map((i) => truncar(i.nome)),
    datasets: [
      {
        data: itens.map((item, idx) => (ocultos.has(idx) ? null : item.valor)),
        backgroundColor: coresItem,
        borderRadius: 4,
        barPercentage: 0.7,
        categoryPercentage: 0.85,
      },
    ],
  };

  const opcoes = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { right: 52, top: 4 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${formatarM(ctx.raw)}`,
          title: (items) => nomesCompletos[items[0].dataIndex],
        },
      },
      datalabels: {
        anchor: "end",
        align: "right",
        color: corTexto,
        font: { size: 11, weight: "bold" },
        formatter: (v) => (v == null ? "" : formatarM(v)),
      },
    },
    scales: {
      x: { display: false, beginAtZero: true },
      y: {
        grid: { display: false },
        border: { display: false },
        ticks: { font: { size: 11, weight: "600" }, color: corTexto },
      },
    },
  };

  return (
    <div
      className="flex-1 min-w-0 rounded-xl bg-white p-6"
      style={{ boxShadow: "0 1px 6px 0 rgba(0,0,0,0.10)" }}
    >
      <h3 className="mb-4 font-bold" style={{ color: corTexto, fontSize: 20 }}>
        Top 5 Clientes Transação
      </h3>
      <div style={{ height: 260 }}>
        <Bar data={dados} options={opcoes} />
      </div>
      <div className="border-t border-[#E5E7EB] -mx-6 mt-3" />
      <div className="mt-3 flex flex-col gap-1">
        {nomesCompletos.map((nome, idx) => (
          <button
            key={idx}
            onClick={() => toggle(idx)}
            className="flex items-center gap-2 text-left text-xs"
            style={{ color: ocultos.has(idx) ? "#9CA3AF" : "#0A0A0A", background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            <span
              style={{
                display: "inline-block",
                width: 14,
                height: 10,
                borderRadius: 2,
                backgroundColor: ocultos.has(idx) ? "#D1D5DB" : coresItem[idx],
                flexShrink: 0,
              }}
            />
            {nome}
          </button>
        ))}
      </div>
    </div>
  );
};



const coresBandeiras = ["#9DA2B6", "#0096FF", "#A8E0FF", "#00034D", "#034EA2"];

const TopBandeirasCashback = ({ itens = [] }) => {
  const [ocultos, setOcultos] = useState(new Set());

  const toggle = (idx) =>
    setOcultos((prev) => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });

  const labels    = itens.map((i) => i.nome);
  const coresItem = itens.map((_, idx) => coresBandeiras[idx] ?? coresBandeiras[0]);

  const dados = {
    labels,
    datasets: [
      {
        data: itens.map((item, idx) => (ocultos.has(idx) ? null : item.valor)),
        backgroundColor: coresItem,
        borderRadius: 4,
        barPercentage: 0.7,
        categoryPercentage: 0.85,
      },
    ],
  };

  const opcoes = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 4 } },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${formatarReais(ctx.raw)}`,
        },
      },
      datalabels: {
        anchor: "center",
        align: "center",
        color: "#fff",
        font: { size: 11, weight: "bold" },
        formatter: (v) => (v == null ? "" : formatarReais(v)),
      },
    },
    scales: {
      x: {
        display: true,
        beginAtZero: true,
        grid: { display: false },
        border: { display: false },
        ticks: {
          font: { size: 10 },
          color: "#364153",
          maxTicksLimit: 3,
          callback: (v) =>
            `R$${new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2 }).format(v)}`,
          maxRotation: 0,
        },
      },
      y: {
        grid: { display: false },
        border: { display: false },
        ticks: { font: { size: 11, weight: "600" }, color: corTexto },
      },
    },
  };

  return (
    <div
      className="flex-1 min-w-0 rounded-xl bg-white p-6"
      style={{ boxShadow: "0 1px 6px 0 rgba(0,0,0,0.10)" }}
    >
      <h3 className="mb-4 font-bold" style={{ color: corTexto, fontSize: 20 }}>
        Top 5 Bandeiras Cashback
      </h3>
      <div style={{ height: 260 }}>
        <Bar data={dados} options={opcoes} />
      </div>
      <div className="border-t border-[#E5E7EB] -mx-6 mt-3" />
      <div className="flex flex-col gap-1 py-2">
        {labels.map((nome, idx) => (
          <button
            key={idx}
            onClick={() => toggle(idx)}
            className="flex items-center gap-2 text-left text-xs"
            style={{ color: ocultos.has(idx) ? "#9CA3AF" : "#0A0A0A", background: "none", border: "none", cursor: "pointer", padding: 0 }}
          >
            <span
              style={{
                display: "inline-block",
                width: 14,
                height: 10,
                borderRadius: 2,
                backgroundColor: ocultos.has(idx) ? "#D1D5DB" : coresItem[idx],
                flexShrink: 0,
              }}
            />
            {nome}
          </button>
        ))}
      </div>
    </div>
  );
};

export { TopClientesTransacao, TopBandeirasCashback };
